import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Appointment } from 'src/app/models/appointment.model';
import { faUser, faCalendar, faLocation,faMapPin, IconDefinition, faChevronDown, faChevronUp, faFileText } from '@fortawesome/free-solid-svg-icons';
import { DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface IDisplayDetail {
  title: string;
  subtitle: string;
  icon: IconDefinition | string;
  additionals?: {title: string; value: string}[];
  additionalsOpen?: boolean
}
@Component({
  selector: 'ubc-appointment-summary',
  templateUrl: './appointment-summary.component.html',
  styleUrls: ['./appointment-summary.component.scss']
})
export class AppointmentSummaryComponent implements OnInit {

  faUser = faUser
  faCalendar = faCalendar
  faLocation = faLocation
  faMapPin = faMapPin
  faChevronDown = faChevronDown
  faChevronUp = faChevronUp
  faFileText = faFileText

  details: IDisplayDetail[] = []

  @Input() appointment: Appointment;
  @Output() editModeRequested = new EventEmitter()
  constructor(
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    console.log("AppointmentSummaryComponent", this.appointment)

    if(!this.appointment){
      return
    }

    const formattedStartEnd = `${this.datePipe.transform(this.appointment.startTime.toDate(), "H:mm")} - ${this.datePipe.transform(this.appointment.endTime.toDate(), "H:mm")}`

    this.details = [
      {
        title: this.appointment.customer.getFullName(),
        subtitle: `<a href="tel:${this.appointment.customer.tel}">${this.appointment.customer.tel}</a><br /><a href="mailto:${this.appointment.customer.email}">${this.appointment.customer.email}</a>`,
        icon: faUser,
        additionals: (this.appointment.customer.notes) ? [
          {
            title: "Notes",
            value: this.appointment.customer.notes
          }
        ] : undefined
      },
      {
        title: formattedStartEnd,
        subtitle: `${this.datePipe.transform(this.appointment.startTime.toDate(), "EEEE, d MMMM y")}<br />${this.appointment.type.name} with ${this.appointment.fitter.name}`,
        icon: faCalendar
      },
      {
        title: this.appointment.address.line1,
        subtitle: this.appointment.address.getFullSummary(),
        icon: faMapPin,
        additionals: (this.appointment.address.notes) ? [
          {
            title: "Notes",
            value: this.appointment.address.notes
          },
          {
            title: "What 3 Words",
            value: (this.appointment.address.w3w) ? `<a href="https://w3w/${this.appointment.address.w3w}">${this.appointment.address.w3w}</a>` : "No what 3 words location has been set for this address"
          }
        ] : undefined
      },
      {
        title: this.appointment.horse.name,
        subtitle: `${this.appointment.horse.breed} ${this.appointment.horse.height} ${this.appointment.horse.sex}`,
        icon: `<svg class="w-6 h-6" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M509.8 332.5l-69.89-164.3c-14.88-41.25-50.38-70.98-93.01-79.24c18-10.63 46.35-35.9 34.23-82.29c-1.375-5.001-7.112-7.972-11.99-6.097l-202.3 75.66C35.89 123.4 0 238.9 0 398.8v81.24C0 497.7 14.25 512 32 512h236.2c23.75 0 39.3-25.03 28.55-46.28l-40.78-81.71V383.3c-45.63-3.5-84.66-30.7-104.3-69.58c-1.625-3.125-.9342-6.951 1.566-9.327l12.11-12.11c3.875-3.875 10.64-2.692 12.89 2.434c14.88 33.63 48.17 57.38 87.42 57.38c17.13 0 33.05-5.091 46.8-13.22l46 63.9c6 8.501 15.75 13.34 26 13.34h50.28c8.501 0 16.61-3.388 22.61-9.389l45.34-39.84C511.6 357.7 514.4 344.2 509.8 332.5zM328.1 223.1c-13.25 0-23.96-10.75-23.96-24c0-13.25 10.75-23.92 24-23.92s23.94 10.73 23.94 23.98C352 213.3 341.3 223.1 328.1 223.1z"/></svg>`
      },
      {
        title: this.appointment.saddleType.name,
        subtitle: (this.appointment.budget) ? `Budget: £${this.appointment.budget}` : '',
        icon: `<svg class="w-6 h-6" width="543.129" height="477.577" viewBox="0 0 543.129 477.577">
  <g id="Group_1" data-name="Group 1" transform="translate(-235.344 -219.21)">
    <path id="Path_1" data-name="Path 1" d="M414.418,252.016c43.941-3.923,66.823,53.468,143.931,49.272,144.112-10.561,177.4-80.415,201.831-81.745,7.75-5.495,19.863,58.571,17.844,72.211-16.736,15.617-82.779,52.288-152.8,80.555-57,18.2-65.81,11.993-118.492,9.289-32.069,6.63-55.14,54.669-142.281,35.285-35.915-19.651-46.539-63.456-49.1-90.062C339.6,291.593,410.357,242.259,414.418,252.016Z"/>
    <path id="Path_2" data-name="Path 2" d="M304.315,341.521c3.493,3.52,9.768,72.689,71.029,92.312,69.421,9.119,103.632-28.258,135.179-41,154.6,12.088,211.024-56.074,267.95-86.547-2.192,29.992-6.123,60.236-52.282,89.148-49.959,35.191-112.192,21.1-132.356,26.5-31.935,223.831-59.8,280.036-188.313,274.493-47.912-5.195-47.926-6.772-49.859-9.664,13.156-25.157,22.878-73.014,13.617-116.252-30.356-91.147-68.867-111.63-86.331-167.966-.3-.979-3.984-24.366-4.309-25.511C288.531,359.455,301.482,341.638,304.315,341.521Z"/>
    <path id="Path_3" data-name="Path 3" d="M271.078,396.535c24.493,74.57,37.172,63.225,79.248,151.009,15.4,61.441,7.909,109.731-5.35,136.745-27.146-2.1-90.837-51.913-105.772-113.67C224.111,487.452,257.2,405.193,271.078,396.535Z"/>
  </g>
</svg>
`
      }
      
    ]

    if(this.appointment.rider){
      this.details.push(
        {
          title: this.appointment.rider?.name || "",
          subtitle: "",
          icon: `<svg class="w-6 h-6" width="463.499" height="744.323" viewBox="0 0 463.499 744.323">
    <g id="Group_1" data-name="Group 1" transform="translate(-584.642 -399.412)">
      <path id="Path_1" data-name="Path 1" d="M594.642,1069.055V819.383s3.074-210.9,218.6-210.9,226.9,210.9,226.9,210.9v249.672Z" transform="translate(-1)" fill="none" stroke="#000" stroke-linejoin="round" stroke-width="18"/>
      <path id="Path_2" data-name="Path 2" d="M594.642,1017.691V795.863s2.432-187.379,172.9-187.379S947,795.863,947,795.863v221.828Z" transform="translate(45.632 51.364)" fill="none" stroke="#000" stroke-linejoin="round" stroke-width="17"/>
      <path id="Path_3" data-name="Path 3" d="M757.234,404.412v204.5" transform="translate(-2)" fill="none" stroke="#000" stroke-linecap="round" stroke-width="10"/>
      <path id="Path_4" data-name="Path 4" d="M876.413,404.412V607.83" transform="translate(-3)" fill="none" stroke="#000" stroke-linecap="round" stroke-width="10"/>
      <path id="Path_5" data-name="Path 5" d="M594.642,1136.235h445.5V1070.57h-445.5Z" transform="translate(-1 -1)" fill="none" stroke="#000" stroke-linejoin="round" stroke-width="17"/>
    </g>
  </svg>
  `
        }
      )
    }


    if(this.appointment.notes){
      this.details.push(
        {
          title: "Notes",
          subtitle: this.appointment.notes,
          icon: faFileText
        }
      )
    }

  }

  /** used in the template to distinguish FA vs. SVG */
  isFaIcon(icon: IconDefinition | string): icon is IconDefinition {
    return typeof icon !== 'string';
  }

  /** sanitize an SVG string so Angular will render it */
  toSafeHtml(svg: string | IconDefinition): SafeHtml {
    // we know at runtime it’s really a string here
    return this.sanitizer.bypassSecurityTrustHtml(svg as string);
  }

  toggleAdditionalsFordetail(detail: IDisplayDetail){
    console.log("toggleAdditionalsFordetail")
    if(!detail.additionals){
      console.log("No additioanls")
      return
     
    }

    if(!detail.additionalsOpen){
      detail.additionalsOpen = true
      console.log("No additionalsOpen")
      return
    }
    console.log("additionalsOpen true")
    detail.additionalsOpen = false
  }

  editClicked(){
    console.log("Edit clicked")
    this.editModeRequested.emit()
  }
}
